import { toast } from 'sonner';

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  CalendarAvailableTimeSlotsPayloadModel,
  CalendarAvailableTimeSlotsResponseModel,
  CalendarPayloadModel,
  CalendarResponseModel,
  DoctorClinicPayloadModel,
  DoctorClinicResponseModel,
  GetInPersonBookingHistoryParams,
  GetInPersonBookingPayloadModel,
  GetInPersonBookingResponseModel,
  InPersonBookingCitiesPayloadModel,
  InPersonBookingCitiesResponseModel,
  InPersonBookingHistoryDetail,
  InPersonBookingHistoryDetailResponse,
  InPersonBookingHistoryResponse,
  InPersonBookingReservePayloadModel,
  InPersonBookingUpdatePayloadModel,
  PaginatedInPersonBookingHistoryResponse,
} from '@models/in-person-booking';

import {
  getCalendar,
  getCalendarAvailableTimeSlots,
  getDoctorClinic,
  getInPersonBooking,
  getInPersonBookingCities,
  getInPersonBookingHistory,
  getInPersonBookingHistoryDetailByRecId,
  inPersonBookingReserve,
  inPersonBookingUpdate,
} from './index';

export const useGetDoctorClinic = (
  body: DoctorClinicPayloadModel,
  options?: Omit<UseQueryOptions<DoctorClinicResponseModel, Error>, 'queryKey'>,
): UseQueryResult<DoctorClinicResponseModel, Error> => {
  return useQuery<DoctorClinicResponseModel, Error>({
    queryKey: ['getDoctorClinic', body],
    queryFn: () => getDoctorClinic(body),
    ...options,
  });
};

export const useGetCalendar = (
  body: CalendarPayloadModel,
  options?: Omit<UseQueryOptions<CalendarResponseModel, Error>, 'queryKey'>,
): UseQueryResult<CalendarResponseModel, Error> => {
  return useQuery<CalendarResponseModel, Error>({
    queryKey: ['getCalendar', body],
    queryFn: () => getCalendar(body),
    ...options,
  });
};

export const useGetCalendarAvailableTimeSlots = (
  body?: CalendarAvailableTimeSlotsPayloadModel,
  options?: Omit<
    UseQueryOptions<CalendarAvailableTimeSlotsResponseModel, Error>,
    'queryKey'
  >,
): UseQueryResult<CalendarAvailableTimeSlotsResponseModel, Error> => {
  return useQuery<CalendarAvailableTimeSlotsResponseModel, Error>({
    queryKey: ['getCalendarAvailableTimeSlots', body],
    queryFn: () => getCalendarAvailableTimeSlots(body),
    ...options,
  });
};

export const useGetInPersonBookingCities = (
  body?: InPersonBookingCitiesPayloadModel,
  options?: Omit<
    UseQueryOptions<InPersonBookingCitiesResponseModel, Error>,
    'queryKey'
  >,
): UseQueryResult<InPersonBookingCitiesResponseModel, Error> => {
  return useQuery<InPersonBookingCitiesResponseModel, Error>({
    queryKey: ['getInPersonBookingCities', body],
    queryFn: () => getInPersonBookingCities(body),
    ...options,
  });
};

export const useGetInPersonBooking = (
  body: GetInPersonBookingPayloadModel,
  options?: Omit<
    UseQueryOptions<GetInPersonBookingResponseModel, Error>,
    'queryKey'
  >,
): UseQueryResult<GetInPersonBookingResponseModel, Error> => {
  return useQuery<GetInPersonBookingResponseModel, Error>({
    queryKey: ['getInPersonBooking', body],
    queryFn: () => getInPersonBooking(body),
    ...options,
  });
};

export const useInPersonBookingReserve = (
  options?: UseMutationOptions<
    undefined,
    Error,
    InPersonBookingReservePayloadModel
  >,
): UseMutationResult<undefined, Error, InPersonBookingReservePayloadModel> => {
  return useMutation({
    mutationFn: inPersonBookingReserve,
    onError: (err: Error) => {
      toast.error(err?.message);
    },
    ...options,
  });
};

export const useInPersonBookingUpdate = (
  options?: UseMutationOptions<
    undefined,
    Error,
    InPersonBookingUpdatePayloadModel
  >,
): UseMutationResult<undefined, Error, InPersonBookingUpdatePayloadModel> => {
  return useMutation({
    mutationFn: inPersonBookingUpdate,
    onError: (err: Error) => {
      toast.error(err?.message);
    },
    ...options,
  });
};

export const useGetInPersonBookingHistory = (
  params?: GetInPersonBookingHistoryParams,
  options?: Partial<
    Omit<
      UseInfiniteQueryOptions<
        InPersonBookingHistoryResponse,
        Error,
        PaginatedInPersonBookingHistoryResponse,
        ['getInPersonBookingHistory', GetInPersonBookingHistoryParams?],
        number
      >,
      'queryKey' | 'queryFn'
    >
  >,
) => {
  return useInfiniteQuery({
    queryKey: ['getInPersonBookingHistory', params],
    queryFn: ({ pageParam = 1 }) =>
      getInPersonBookingHistory({
        ...params,
        pageNumber: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { totalCount = 0, pageNumber = 1, pageSize = 10 } = lastPage.data;

      if (totalCount <= 0) {
        return undefined;
      }

      const totalPages = Math.ceil(totalCount / pageSize);

      return pageNumber < totalPages ? pageNumber + 1 : undefined;
    },
    ...options,
  });
};

export const useGetInPersonBookingHistoryDetailByRecId = (
  recId?: number,
  options?: Omit<
    UseQueryOptions<
      InPersonBookingHistoryDetailResponse,
      Error,
      InPersonBookingHistoryDetail
    >,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) => {
  return useQuery({
    queryKey: ['getInPersonBookingHistoryDetail', recId],
    queryFn: () => getInPersonBookingHistoryDetailByRecId(recId!),
    enabled: !!recId,
    select: (response) => response.data,
    ...options,
  });
};
